import {
	ChainStrategy,
	type GenerationFailureReason,
	type GenerationJob,
	GenerationJobStatus,
	GenerationKind,
	RenderMode,
} from "@storyline/shared";
import type { OrderedShot } from "@/src/stories/utils/ordered-shots";

export const FrameStatus: {
	GENERATED: "generated";
	INHERITED: "inherited";
	NOT_REQUIRED: "not_required";
	PENDING: "pending";
	FAILED: "failed";
} = {
	GENERATED: "generated",
	INHERITED: "inherited",
	NOT_REQUIRED: "not_required",
	PENDING: "pending",
	FAILED: "failed",
};

export type FrameStatus = (typeof FrameStatus)[keyof typeof FrameStatus];

export type FrameSlotKind = "entry" | "exit";

export type FrameSlotState = {
	slot: FrameSlotKind;
	generationKind: GenerationKind;
	status: FrameStatus;
	imageUrl: string | null;
	inheritedFromUrl: string | null;
	failureReason: GenerationFailureReason | null;
	isBusy: boolean;
};

export type ShotFrameState = {
	entry: FrameSlotState;
	exit: FrameSlotState;
};

export type FrameSummary = {
	generated: number;
	inherited: number;
	missing: number;
	failed: number;
};

const isActive = (job: GenerationJob): boolean =>
	job.status === GenerationJobStatus.PENDING || job.status === GenerationJobStatus.PROCESSING;

export const isShotVideoBusy = (shotId: string, jobs: GenerationJob[]): boolean =>
	jobs.some((job) => job.targetId === shotId && job.kind === GenerationKind.SHOT_VIDEO && isActive(job));

const resolveGeneratedSlot = (
	slot: FrameSlotKind,
	generationKind: GenerationKind,
	shotId: string,
	imageUrl: string | null,
	jobs: GenerationJob[],
): FrameSlotState => {
	const relevant = jobs.filter((job) => job.targetId === shotId && job.kind === generationKind);
	const busy = relevant.some(isActive);
	if (busy) {
		return { slot, generationKind, status: FrameStatus.PENDING, imageUrl, inheritedFromUrl: null, failureReason: null, isBusy: true };
	}
	if (imageUrl) {
		return { slot, generationKind, status: FrameStatus.GENERATED, imageUrl, inheritedFromUrl: null, failureReason: null, isBusy: false };
	}
	const latestFailed = relevant
		.filter((job) => job.status === GenerationJobStatus.FAILED)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
	if (latestFailed) {
		return {
			slot,
			generationKind,
			status: FrameStatus.FAILED,
			imageUrl: null,
			inheritedFromUrl: null,
			failureReason: latestFailed.failureReason,
			isBusy: false,
		};
	}
	return { slot, generationKind, status: FrameStatus.PENDING, imageUrl: null, inheritedFromUrl: null, failureReason: null, isBusy: false };
};

export const shotFrameState = (shot: OrderedShot["shot"], previousTailUrl: string | null, jobs: GenerationJob[]): ShotFrameState => {
	const entry: FrameSlotState =
		shot.chainStrategy === ChainStrategy.INHERIT_TAIL
			? {
					slot: "entry",
					generationKind: GenerationKind.KEYFRAME,
					status: FrameStatus.INHERITED,
					imageUrl: shot.startFrameUrl,
					inheritedFromUrl: shot.startFrameUrl ?? previousTailUrl,
					failureReason: null,
					isBusy: false,
				}
			: resolveGeneratedSlot("entry", GenerationKind.KEYFRAME, shot.id, shot.startFrameUrl, jobs);

	const exit: FrameSlotState =
		shot.renderMode === RenderMode.PREMIUM_VIDEO
			? resolveGeneratedSlot("exit", GenerationKind.END_KEYFRAME, shot.id, shot.endFrameUrl, jobs)
			: {
					slot: "exit",
					generationKind: GenerationKind.END_KEYFRAME,
					status: FrameStatus.NOT_REQUIRED,
					imageUrl: null,
					inheritedFromUrl: null,
					failureReason: null,
					isBusy: false,
				};

	return { entry, exit };
};

export type ContactSheetEntry = {
	ordered: OrderedShot;
	state: ShotFrameState;
};

export const contactSheetStates = (ordered: OrderedShot[], jobs: GenerationJob[]): ContactSheetEntry[] => {
	let previousTailUrl: string | null = null;
	return ordered.map((item) => {
		const state = shotFrameState(item.shot, previousTailUrl, jobs);
		previousTailUrl = item.shot.endFrameUrl ?? item.shot.startFrameUrl ?? previousTailUrl;
		return { ordered: item, state };
	});
};

export const summarizeFrames = (states: ShotFrameState[]): FrameSummary => {
	const summary: FrameSummary = { generated: 0, inherited: 0, missing: 0, failed: 0 };
	for (const state of states) {
		for (const slot of [state.entry, state.exit]) {
			if (slot.status === FrameStatus.GENERATED) {
				summary.generated += 1;
			} else if (slot.status === FrameStatus.INHERITED) {
				summary.inherited += 1;
			} else if (slot.status === FrameStatus.PENDING) {
				summary.missing += 1;
			} else if (slot.status === FrameStatus.FAILED) {
				summary.failed += 1;
			}
		}
	}
	return summary;
};
